import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Divider, NumberFormatter } from '@mantine/core';
import dayjs from 'dayjs';
import AppStack from '../../common/AppStack';
import ResponsiveModal from '../../common/ResponsiveModal';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import { CURRENCY_TYPE_LIST } from '../../../config/constants';
import { formatLocalDate } from '../../../helpers/format';
import { triggerNotification } from '../../../helpers/notificationHelper';

const OrderPaymentInstallmentProcessConfirmModal = ({
  isOpen,
  onClose,
  onRefreshData,
  pkRegCart,
  payment,
  installment
}) => {
  const { processRegCartPaymentInstallmentImmediately } = useContext(
    RegistrationAdminContext
  );
  const [formState, setFormState] = useState({ loading: false });

  const currency = CURRENCY_TYPE_LIST.find(
    (c) => c.value === payment?.fkCurrencyType
  );
  const currencyAbbr = currency?.abbr || null;

  useEffect(() => {
    if (isOpen) {
      setFormState({ loading: false });
    }
  }, [isOpen]);

  return (
    <ResponsiveModal
      formSectionProps={{
        cancelTitle: 'Close',
        onCancel: onClose,
        submitTitle: `Yes, process $${(installment?.chargeAmount ?? 0) / 100}`,
        submitLoading: formState.loading,
        onSubmit: () => {
          setFormState({ loading: true });
          processRegCartPaymentInstallmentImmediately(
            pkRegCart,
            { pkRegCartPaymentInstallment: installment.entityId },
            () => {
              triggerNotification('Payment processed', 'Success', 'green');
              onRefreshData?.();
              onClose();
            },
            (e) => {
              triggerNotification(e);
              setFormState({ loading: false });
              onRefreshData?.();
            }
          );
        }
      }}
      isNewModalStyle
      isOpen={isOpen}
      onClose={onClose}
      size={500}
      title="Process installment now?"
    >
      {!installment ? (
        <AppStack>
          <AppText
            style={{
              fontSize: 14,
              textAlign: 'center',
              color: '#666',
              fontStyle: 'italic'
            }}
          >
            No installment selected.
          </AppText>
        </AppStack>
      ) : (
        <AppStack style={{ gap: 16 }}>
          <AppStack style={{ gap: 12 }}>
            <AppText style={{ fontSize: 14 }}>
              Are you sure you want to{' '}
              <span style={{ fontWeight: 500 }}>process</span> this installment
              now?
            </AppText>

            <AppText style={{ fontSize: 14 }}>
              This will charge the customer's payment method immediately and
              mark this installment as paid.
            </AppText>

            <AppText
              style={{
                fontSize: 14,
                fontWeight: 500
              }}
            >
              This action cannot be undone.
            </AppText>
          </AppStack>

          <Divider />

          <AppStack style={{ gap: 12 }}>
            <AppFlexbox
              style={{
                flexWrap: 'nowrap',
                gap: 10,
                alignItems: 'center'
              }}
            >
              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                Charge amount
              </AppText>
              <Divider style={{ flex: 1 }} variant="dotted" />
              <AppText style={{ fontSize: 16, color: '#37b24d' }}>
                <NumberFormatter
                  decimalScale={2}
                  fixedDecimalScale
                  prefix="$"
                  style={{ fontSize: 16 }}
                  suffix={` ${currencyAbbr}`}
                  thousandSeparator
                  value={installment.chargeAmount / 100}
                />
              </AppText>
            </AppFlexbox>

            <AppFlexbox
              style={{
                flexWrap: 'nowrap',
                gap: 10,
                alignItems: 'center'
              }}
            >
              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                Original date
              </AppText>
              <Divider style={{ flex: 1 }} variant="dotted" />
              <AppText style={{ fontSize: 16 }}>
                {dayjs(formatLocalDate(installment.dueDate)).format(
                  'MMMM D, YYYY'
                )}
              </AppText>
            </AppFlexbox>
          </AppStack>

          <Divider />

          <AppText
            style={{
              fontSize: 14,
              color: '#666',
              textAlign: 'center',
              fontStyle: 'italic'
            }}
          >
            Any remaining installments in the payment plan will not be affected.
          </AppText>
        </AppStack>
      )}
    </ResponsiveModal>
  );
};

OrderPaymentInstallmentProcessConfirmModal.propTypes = {
  installment: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onRefreshData: PropTypes.func,
  payment: PropTypes.object,
  pkRegCart: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default OrderPaymentInstallmentProcessConfirmModal;
